import * as React from "react";

import { AccessReportsService } from "@app/access-reports";
import { TemplateNode, TemplateNodeTypeEnum } from "@app/access-reports/admin/template-update";
import { AccessReportsWizardTemplate } from "@app/access-reports/shared";
import { LoadingSpinner } from "@app/shared";
import { QESSelect } from "@app/shared/qes-standards";
import { flatten } from "lodash";

interface TemplateNodeSelectionProps {
  selectedClients: string[];
  selectedNode: TemplateNode;
  selectedNodeType: TemplateNodeTypeEnum;
  setSelectedNode: React.Dispatch<React.SetStateAction<TemplateNode>>;
  setSelectedNodeType: React.Dispatch<React.SetStateAction<TemplateNodeTypeEnum>>;
  setTemplate: React.Dispatch<React.SetStateAction<AccessReportsWizardTemplate>>;
  template: AccessReportsWizardTemplate;
}

export const TemplateNodeSelection: React.FC<React.PropsWithChildren<TemplateNodeSelectionProps>> = (props: TemplateNodeSelectionProps) => {
  const accessReportsService = new AccessReportsService();

  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    try {
      if (!loading) {
        setLoading(true);
      }

      // currently, there is no way to retrieve an access report template by client id
      // temporarily hard coding a template, FOR NOW
      accessReportsService.getAccessReportsWizard().then((x) => props.setTemplate(x?.accessReportWizardVersions[0]?.template));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [props.selectedClients]);

  const nodeOptions: TemplateNode[] = React.useMemo(() => {
    let options: TemplateNode[] = [];
    let optionId = 0;

    if (!!props.selectedNodeType) {
      switch (props.selectedNodeType) {
        case TemplateNodeTypeEnum.Page:
          options = props.template.pages.map((page) => {
            optionId = optionId + 1;

            return {
              displayName: page.title,
              id: optionId,
              nodeId: page.title,
              parentNodeTitle: null,
            } as TemplateNode;
          });

          break;
        case TemplateNodeTypeEnum.FormGroup:
          options = flatten(props.template.pages.map((page) => page.formGroups.map((formGroup) => {
            optionId = optionId + 1;

            return {
              displayName: !!formGroup.groupPropertyId ? formGroup.groupPropertyId : formGroup.title ? formGroup.title : `${page.title} - Form Group: ${formGroup.id + 1}`,
              id: optionId,
              nodeId: formGroup.id.toString(),
              parentNodeTitle: page.title,
            } as TemplateNode;
          })));

          break;
        default:
          console.error(`Invalid Template Node Type: ${props.selectedNodeType}`)
          break;
      }
    }

    return options;
  }, [props.selectedNodeType, props.template]);

  const updateSelectedNodes = (selectedNodeId: string): void => {
    const selectedNode: TemplateNode = nodeOptions.find(x => x.id.toString() === selectedNodeId);

    props.setSelectedNode(selectedNode);
  }

  const updateSelectedNodeType = (templateNodeType: TemplateNodeTypeEnum): void => {
    props.setSelectedNode(null);
    props.setSelectedNodeType(templateNodeType);
  }

  return (
    <div className="row">
      <div className="col-6">
        <QESSelect
          allowEmpty={true}
          className="mb-3"
          disabled={!props.selectedClients.length}
          testId="access-reports-admin__template-update--node-type-select"
          label="Select a node type"
          onChange={(e) => updateSelectedNodeType(e.target.value as TemplateNodeTypeEnum)}
          value={props.selectedNodeType || ""}
        >
          {Object.values(TemplateNodeTypeEnum).filter(x => x !== TemplateNodeTypeEnum.Element).map((option, optionIndex) => (
            <option
              key={optionIndex}
              value={option}
            >
              {option}
            </option>
          ))}
        </QESSelect>
      </div>

      <div className="col-6">
        {loading ? (
          <div className="text-center">
            <LoadingSpinner />
          </div>
        ) : (
          <QESSelect
            allowEmpty={true}
            className="mb-3"
            disabled={(!props.selectedClients.length || !props.selectedNodeType)}
            testId="access-reports-admin__template-update--node-select"
            label="Select a node"
            onChange={(e) => updateSelectedNodes(e.target.value)}
            value={props.selectedNode?.id || ""}
          >
            {nodeOptions.map((option, optionIndex) => (
              <option
                key={optionIndex}
                value={option.id}
              >
                {option.displayName}
              </option>
            ))}
          </QESSelect>
        )}
      </div>
    </div>
  );
}
